import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import SalesminerAppLayout from "@/salesminer/layouts/SalesminerAppLayout.tsx";
import PageHeader from "@/salesminer/layouts/app/PageHeader.tsx";

interface FaqItem {
    id: number
    question: string
    answer: string
}

interface FaqProps {
    faq_data: FaqItem[]
}

function FAQItem({faq, isOpen, onToggle}: { faq: FaqItem; isOpen: boolean; onToggle: () => void }) {
    return <>
        <div className="border-b border-neutral-200 dark:border-dark-border-light">
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between px-6 py-4 text-left transition-colors duration-200"
            >
                <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 pr-4">
                    {faq.question}
                </h3>
                <div className="flex-shrink-0">
                    {isOpen ? (
                        <ChevronUp className="size-5 text-neutral-600 dark:text-neutral-400"/>
                    ) : (
                        <ChevronDown className="size-5 text-neutral-600 dark:text-neutral-400"/>
                    )}
                </div>
            </button>

            {isOpen && (
                <div className="px-6 pb-4">
                    <div className="text-neutral-700 dark:text-neutral-300 leading-relaxed whitespace-pre-line">
                        {faq.answer}
                    </div>
                </div>
            )}
        </div>
    </>
}

export default function FaqIndex({faq_data}: FaqProps) {
    const [openItems, setOpenItems] = useState(new Set<number>())

    const toggleItem = (id: number) => {
        const newOpenItems = new Set(openItems)
        if (newOpenItems.has(id)) {
            newOpenItems.delete(id)
        } else {
            newOpenItems.add(id)
        }
        setOpenItems(newOpenItems)
    }

    return (
        <SalesminerAppLayout>
            <PageHeader
                title="Frequently Asked Questions"
                subtitle="Find answers to common questions about SALESminer and how it can help your business."
            />

            {/* FAQ Items */}
            <div
                className="rounded-lg shadow-sm border border-neutral-200 dark:border-dark-border-light overflow-hidden">
                {faq_data.map((faq) => (
                    <FAQItem
                        key={faq.id}
                        faq={faq}
                        isOpen={openItems.has(faq.id)}
                        onToggle={() => toggleItem(faq.id)}
                    />
                ))}
            </div>

            {/* Contact Section */}
            <div
                className="mt-8 bg-link/10 dark:bg-link/20 text-link dark:text-white rounded-lg p-6 border border-accent">
                <h2 className="text-xl font-semibold text-link dark:text-blue-100 mb-2">
                    Still have questions?
                </h2>
                <p className="text-link dark:text-blue-200 mb-4">
                    If you couldn't find the answer you're looking for, please don't hesitate to contact our support
                    team.
                </p>
                <a
                    href="mailto:support@vitelis.com"
                    className="inline-flex items-center px-4 py-2 bg-accent text-button-text hover:bg-accent/90 focus:bg-accent/90 rounded-md transition-colors duration-200 font-medium focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Contact Support
                </a>
            </div>
        </SalesminerAppLayout>
    )
}